/**
 * Importación masiva (cliente-side, sin IA / sin créditos) de los maestros:
 * proveedores, clientes y catálogo de contrapartidas.
 *
 * Lee Excel/CSV con ExcelJS, normaliza los valores con las mismas reglas que
 * usa la edición manual (NIF, atajos de cuenta, tipos de operación/retención)
 * y hace alta/actualización por lotes.
 */
import ExcelJS from "exceljs";
import { supabase } from "@/integrations/supabase/client";
import { expandAccountShortcut } from "@/lib/accountShortcut";
import { normalizeOperationType, normalizeRetentionType } from "@/lib/supplierMaster";
import { normalizeNif } from "@/lib/customerMaster";

export type MasterKind = "suppliers" | "customers" | "catalog";

export type FieldType = "text" | "nif" | "account" | "postal" | "operation" | "retention";

export interface MasterField {
  key: string;
  /** Columna en la tabla de base de datos. */
  column: string;
  label: string;
  type: FieldType;
  required?: boolean;
  patterns: RegExp[];
}

export interface MasterSchema {
  kind: MasterKind;
  table: "supplier_master" | "customer_master" | "company_account_catalog";
  label: string;
  /** Campo que identifica de forma única la fila dentro de la empresa. */
  keyField: string;
  /** Campo alternativo cuando el principal viene vacío (p.ej. cuentas genéricas sin NIF). */
  altKeyField?: string;
  fields: MasterField[];
}

const NIF_PATTERNS = [/nif/i, /cif/i, /n\.?i\.?f/i, /identific/i, /documento/i, /dni/i];
const NAME_PATTERNS = [/descrip/i, /nombre/i, /raz[oó]n/i, /titular/i, /proveedor/i, /cliente/i];
const POSTAL_PATTERNS = [/c\.?\s*p\.?$/i, /c[oó]digo\s*postal/i, /^cp$/i, /postal/i];
const OPERATION_PATTERNS = [/tipo\s*op/i, /operaci[oó]n/i, /intracom/i];

export const MASTER_SCHEMAS: Record<MasterKind, MasterSchema> = {
  suppliers: {
    kind: "suppliers",
    table: "supplier_master",
    label: "proveedores",
    keyField: "taxId",
    altKeyField: "accountCode",
    fields: [
      { key: "taxId", column: "tax_id", label: "NIF / CIF", type: "nif", patterns: NIF_PATTERNS },
      { key: "description", column: "description", label: "Nombre / descripción", type: "text", required: true, patterns: NAME_PATTERNS },
      { key: "accountCode", column: "account_code", label: "Cuenta de proveedor (400/410)", type: "account", required: true, patterns: [/cuenta\s*(de\s*)?proveedor/i, /^cuenta$/i, /subcuenta/i, /c[oó]digo\s*cuenta/i] },
      { key: "expenseAccount", column: "expense_account", label: "Contrapartida / cuenta de gasto", type: "account", patterns: [/contrapartida/i, /gasto/i, /compra/i, /cuenta\s*(de\s*)?(gasto|compra)/i] },
      { key: "postalCode", column: "postal_code", label: "Código postal", type: "postal", patterns: POSTAL_PATTERNS },
      { key: "operationType", column: "operation_type", label: "Tipo de operación", type: "operation", patterns: OPERATION_PATTERNS },
      { key: "retentionType", column: "retention_type", label: "Tipo de retención", type: "retention", patterns: [/retenci[oó]n/i, /irpf/i, /modelo\s*11[15]/i] },
    ],
  },
  customers: {
    kind: "customers",
    table: "customer_master",
    label: "clientes",
    keyField: "taxId",
    altKeyField: "accountCode",
    fields: [
      { key: "taxId", column: "tax_id", label: "NIF / CIF", type: "nif", patterns: NIF_PATTERNS },
      { key: "description", column: "description", label: "Nombre / descripción", type: "text", required: true, patterns: NAME_PATTERNS },
      { key: "accountCode", column: "account_code", label: "Cuenta de cliente (430)", type: "account", required: true, patterns: [/cuenta\s*(de\s*)?cliente/i, /^cuenta$/i, /subcuenta/i, /c[oó]digo\s*cuenta/i] },
      { key: "salesAccount", column: "sales_account", label: "Cuenta de venta (700)", type: "account", patterns: [/venta/i, /ingreso/i, /contrapartida/i] },
      { key: "postalCode", column: "postal_code", label: "Código postal", type: "postal", patterns: POSTAL_PATTERNS },
      { key: "operationType", column: "operation_type", label: "Tipo de operación", type: "operation", patterns: OPERATION_PATTERNS },
    ],
  },
  catalog: {
    kind: "catalog",
    table: "company_account_catalog",
    label: "contrapartidas",
    keyField: "account",
    fields: [
      { key: "account", column: "account", label: "Cuenta", type: "account", required: true, patterns: [/^cuenta$/i, /subcuenta/i, /c[oó]digo/i, /cuenta/i] },
      { key: "description", column: "description", label: "Descripción", type: "text", required: true, patterns: NAME_PATTERNS },
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Lectura de archivo                                                  */
/* ------------------------------------------------------------------ */

export interface ParsedWorkbook {
  sheetNames: string[];
  sheets: unknown[][][];
}

const normalizeCellValue = (v: unknown): unknown => {
  if (v == null) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("result" in o) return o.result;
    if ("text" in o) return o.text;
    if ("richText" in o) return (o.richText as { text: string }[]).map((r) => r.text).join("");
    return String(v);
  }
  return v;
};

export const parseWorkbook = async (file: File): Promise<ParsedWorkbook> => {
  const buf = await file.arrayBuffer();
  if (file.name.toLowerCase().endsWith(".csv")) {
    const text = new TextDecoder().decode(buf);
    const sep = (text.split("\n")[0] || "").includes(";") ? ";" : ",";
    const rows = text.split(/\r?\n/).map((l) => l.split(sep).map((c) => c.replace(/^"|"$/g, "")));
    const maxCols = Math.max(0, ...rows.map((r) => r.length));
    const padded = rows.map((r) => [...r, ...Array(Math.max(0, maxCols - r.length)).fill("")]);
    return { sheetNames: ["CSV"], sheets: [padded] };
  }

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheets: unknown[][][] = [];
  const sheetNames: string[] = [];
  wb.eachSheet((ws) => {
    sheetNames.push(ws.name);
    const grid: unknown[][] = [];
    ws.eachRow({ includeEmpty: true }, (row) => {
      const arr: unknown[] = [];
      row.eachCell({ includeEmpty: true }, (cell) => arr.push(normalizeCellValue((cell as { value: unknown }).value)));
      grid.push(arr);
    });
    const maxCols = Math.max(0, ...grid.map((r) => r.length));
    sheets.push(grid.map((r) => [...r, ...Array(Math.max(0, maxCols - r.length)).fill("")]));
  });
  return { sheetNames, sheets };
};

export const indexToColumnLetter = (idx: number): string => {
  let result = "";
  let n = idx + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result || "A";
};

/* ------------------------------------------------------------------ */
/* Normalización y construcción de filas                               */
/* ------------------------------------------------------------------ */

const cellToString = (v: unknown): string => {
  if (v == null) return "";
  if (v instanceof Date) return v.toLocaleDateString("es-ES");
  if (typeof v === "number") return String(v);
  return String(v).trim();
};

export const normalizeFieldValue = (field: MasterField, raw: unknown, accountLength: number): string => {
  const s = cellToString(raw);
  switch (field.type) {
    case "nif":
      return normalizeNif(s);
    case "account": {
      const expanded = expandAccountShortcut(s, accountLength);
      // Excel suele convertir cuentas en números y perder ceros a la izquierda.
      const digits = expanded.replace(/\s/g, "");
      if (/^\d+$/.test(digits) && digits.length < accountLength) {
        return digits.padEnd(accountLength, "0");
      }
      return digits;
    }
    case "postal": {
      const d = s.replace(/\D/g, "");
      if (!d) return "";
      // Excel pierde el cero inicial en CPs de provincias 01-09.
      return (d.length === 4 ? `0${d}` : d).slice(0, 5);
    }
    case "operation":
      return s ? normalizeOperationType(mapOperationLabel(s)) : "interior";
    case "retention":
      return s ? normalizeRetentionType(mapRetentionLabel(s)) : "none";
    default:
      return s;
  }
};

const mapOperationLabel = (s: string): string => {
  const v = s.toLowerCase();
  if (/intra/.test(v) && /serv/.test(v)) return "ais";
  if (/intra/.test(v)) return "aib";
  if (/isp|sujeto\s*pasivo/.test(v)) return "isp";
  if (/import/.test(v)) return "importacion";
  return v;
};

const mapRetentionLabel = (s: string): string => {
  const v = s.toLowerCase();
  if (/193|123|mobiliario/.test(v)) return "mod193";
  if (/115|arrend|inmobiliario|180/.test(v)) return "mod115";
  if (/111|profesional|trabajo/.test(v)) return "mod111";
  return v;
};

/**
 * Clave única de la fila. Si el NIF viene vacío (cuentas genéricas: proveedores
 * varios, clientes de facturas simplificadas...) se usa la cuenta contable.
 */
export const rowKey = (schema: MasterSchema, values: Record<string, string>): string => {
  const main = values[schema.keyField];
  if (main) return main;
  const alt = schema.altKeyField ? values[schema.altKeyField] : "";
  return alt ? `#${alt}` : "";
};

export interface BuiltRow {
  index: number;
  values: Record<string, string>;
  errors: string[];
  warnings: string[];
}

export const buildRows = (
  grid: unknown[][],
  headerRow: number,
  mapping: Record<string, number | null>,
  schema: MasterSchema,
  accountLength: number,
): BuiltRow[] => {
  const rows: BuiltRow[] = [];
  const seen = new Set<string>();
  const seenAccounts = new Set<string>();

  for (let i = headerRow; i < grid.length; i++) {
    const r = grid[i];
    if (!r || r.every((c) => c == null || c === "")) continue;

    const values: Record<string, string> = {};
    for (const f of schema.fields) {
      const col = mapping[f.key];
      values[f.key] = col == null ? (f.type === "operation" ? "interior" : f.type === "retention" ? "none" : "") : normalizeFieldValue(f, r[col], accountLength);
    }

    const errors: string[] = [];
    const warnings: string[] = [];
    for (const f of schema.fields) {
      if (f.required && !values[f.key]) errors.push(`Falta ${f.label}`);
    }
    const key = rowKey(schema, values);
    if (key) {
      if (seen.has(key)) {
        // Duplicado por cuenta contable: se acepta, solo se avisa.
        if (key.startsWith("#")) warnings.push("Cuenta repetida en el archivo");
        else errors.push("Duplicado dentro del archivo");
      } else seen.add(key);
    }

    const account = schema.altKeyField ? values[schema.altKeyField] : "";
    if (account) {
      if (seenAccounts.has(account) && !warnings.length) warnings.push("Cuenta repetida en el archivo");
      seenAccounts.add(account);
    }

    // Ignora filas totalmente vacías en los campos obligatorios
    const hasAnyRequired = schema.fields.some((f) => f.required && values[f.key]);
    if (!hasAnyRequired) continue;

    rows.push({ index: i + 1, values, errors, warnings });
  }
  return rows;
};


/* ------------------------------------------------------------------ */
/* Escritura                                                           */
/* ------------------------------------------------------------------ */

export interface ImportSummary {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
}

const toPayload = (schema: MasterSchema, values: Record<string, string>, companyId: string) => {
  const payload: Record<string, unknown> = { company_id: companyId };
  for (const f of schema.fields) {
    const v = values[f.key];
    if (f.type === "postal") payload[f.column] = v || null;
    else payload[f.column] = v ?? "";
  }
  return payload;
};

/** Decide a qué maestro va una subcuenta según su grupo del PGC. */
export const targetKindForAccount = (account: string): MasterKind => {
  const a = (account || "").trim();
  if (/^4[01]/.test(a)) return "suppliers";
  if (/^4[34]/.test(a)) return "customers";
  return "catalog";
};

/** Reparte filas de proveedores/clientes según el grupo de su subcuenta. */
export const partitionRowsByAccount = (rows: BuiltRow[]): Record<MasterKind, BuiltRow[]> => {
  const out: Record<MasterKind, BuiltRow[]> = { suppliers: [], customers: [], catalog: [] };
  for (const r of rows) {
    const target = targetKindForAccount(r.values.accountCode || r.values.account || "");
    if (target === "catalog") {
      out.catalog.push({ ...r, values: { account: (r.values.accountCode || r.values.account || "").trim(), description: r.values.description || "" } });
    } else {
      out[target].push({ ...r, values: { ...r.values, accountCode: r.values.accountCode || r.values.account || "" } });
    }
  }
  return out;
};

/**
 * Importa clasificando cada fila por su subcuenta: 40/41 → proveedores,
 * 43/44 → clientes, resto → cuentas generales. Nunca guarda una cuenta
 * general como proveedor o cliente.
 */
export const importMasterRows = async (
  schema: MasterSchema,
  companyId: string,
  rows: BuiltRow[],
  onExisting: "update" | "skip",
  onProgress?: (done: number, total: number) => void,
): Promise<ImportSummary> => {
  const valid = rows.filter((r) => r.errors.length === 0);
  const parts = partitionRowsByAccount(valid);
  const total: ImportSummary = { created: 0, updated: 0, skipped: 0, failed: rows.length - valid.length, errors: [] };
  const grand = valid.length;
  let base = 0;
  for (const kind of ["suppliers", "customers", "catalog"] as MasterKind[]) {
    const list = parts[kind];
    if (list.length === 0) continue;
    const s = await importMasterRowsInto(MASTER_SCHEMAS[kind], companyId, list, onExisting, (d) => onProgress?.(base + d, grand));
    base += list.length;
    total.created += s.created; total.updated += s.updated; total.skipped += s.skipped; total.failed += s.failed;
    total.errors.push(...s.errors);
  }
  void schema;
  return total;
};

const importMasterRowsInto = async (
  schema: MasterSchema,
  companyId: string,
  rows: BuiltRow[],
  onExisting: "update" | "skip",
  onProgress?: (done: number, total: number) => void,
): Promise<ImportSummary> => {
  const summary: ImportSummary = { created: 0, updated: 0, skipped: 0, failed: 0, errors: [] };
  const valid = rows.filter((r) => r.errors.length === 0);
  summary.failed = rows.length - valid.length;

  const keyColumn = schema.fields.find((f) => f.key === schema.keyField)!.column;
  const altColumn = schema.altKeyField
    ? schema.fields.find((f) => f.key === schema.altKeyField)!.column
    : null;

  // Índice de existentes por clave: lista de ids para emparejar cada fila
  // del archivo con un registro distinto (cuentas repetidas sin NIF).
  const existing = new Map<string, string[]>();
  {
    const PAGE = 1000;
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(schema.table)
        .select(altColumn ? `id, ${keyColumn}, ${altColumn}` : `id, ${keyColumn}`)
        .eq("company_id", companyId)
        .range(from, from + PAGE - 1);
      if (error) {
        summary.errors.push(error.message);
        break;
      }
      const list = (data || []) as unknown as Record<string, string>[];
      for (const row of list) {
        const raw = row[keyColumn] || "";
        const norm = keyColumn === "tax_id" ? normalizeNif(raw) : String(raw).trim();
        if (norm) {
          const list = existing.get(norm);
          if (list) list.push(row.id);
          else existing.set(norm, [row.id]);
        } else if (altColumn) {
          const alt = String(row[altColumn] || "").trim();
          if (alt) {
            const list = existing.get(`#${alt}`);
            if (list) list.push(row.id);
            else existing.set(`#${alt}`, [row.id]);
          }
        }
      }
      if (list.length < PAGE) break;
      from += PAGE;
    }
  }

  const toInsert: Record<string, unknown>[] = [];
  const toUpdate: { id: string; payload: Record<string, unknown> }[] = [];

  for (const row of valid) {
    const key = rowKey(schema, row.values);
    const list = existing.get(key);
    const id = list && list.length > 0 ? list.shift()! : null;

    const payload = toPayload(schema, row.values, companyId);
    if (id) {
      if (onExisting === "skip") summary.skipped++;
      else toUpdate.push({ id, payload });
    } else {
      // Sin registro existente disponible: se inserta como nuevo (p. ej.
      // filas repetidas del archivo que superan a las ya guardadas).
      toInsert.push(payload);
    }
  }


  const total = toInsert.length + toUpdate.length;
  let done = 0;

  const CHUNK = 200;
  for (let i = 0; i < toInsert.length; i += CHUNK) {
    const slice = toInsert.slice(i, i + CHUNK);
    const { error } = await supabase.from(schema.table).insert(slice as never);
    if (error) {
      summary.failed += slice.length;
      if (summary.errors.length < 5) summary.errors.push(error.message);
    } else {
      summary.created += slice.length;
    }
    done += slice.length;
    onProgress?.(done, total);
  }

  for (let i = 0; i < toUpdate.length; i += 25) {
    const slice = toUpdate.slice(i, i + 25);
    const results = await Promise.all(
      slice.map((u) => supabase.from(schema.table).update(u.payload as never).eq("id", u.id)),
    );
    results.forEach((res) => {
      if (res.error) {
        summary.failed++;
        if (summary.errors.length < 5) summary.errors.push(res.error.message);
      } else summary.updated++;
    });
    done += slice.length;
    onProgress?.(done, total);
  }

  return summary;
};

/** Genera un CSV con las filas rechazadas para que el usuario las corrija. */
export const buildRejectedCsv = (schema: MasterSchema, rows: BuiltRow[]): string => {
  const header = ["Fila", ...schema.fields.map((f) => f.label), "Motivo"].join(";");
  const lines = rows.map((r) =>
    [r.index, ...schema.fields.map((f) => (r.values[f.key] || "").replace(/;/g, ",")), r.errors.join(" · ")].join(";"),
  );
  return [header, ...lines].join("\n");
};
